import { authService } from '../services/auth.service';

/**
 * Authentication Utility Functions
 * Handles common authentication scenarios and user feedback
 */

/**
 * Handle authentication errors and redirect to login if necessary
 * @param error - The error object from API calls
 * @param redirectToLogin - Function to redirect to login page
 */
export const handleAuthError = (error: any, redirectToLogin: () => void) => {
  if (error?.response?.status === 401) {
    // Check if refresh token is expired
    if (authService.isRefreshTokenExpired()) {
      console.warn('Refresh token expired, redirecting to login');
      redirectToLogin();
      return;
    }
    
    // Try to refresh token
    authService.refreshToken()
      .then(() => {
        // The original request can be retried now
      })
      .catch(() => {
        redirectToLogin();
      });
  } else if (error?.response?.status === 403) {
    console.warn('Access forbidden - user may not have required permissions');
    // Handle 403 errors (could redirect to unauthorized page or show message)
  }
};

/**
 * Check if user should be redirected to login
 * @returns true if user should be redirected, false otherwise
 */
export const shouldRedirectToLogin = (): boolean => {
  const tokenStatus = authService.getTokenStatus();
  
  // Only redirect if:
  // 1. No tokens at all, OR
  // 2. Refresh token is expired (this means we can't refresh access token)
  // 
  // DO NOT redirect just because access token is expired - 
  // the system should handle that by refreshing it
  return !tokenStatus.hasAccessToken || 
         !tokenStatus.hasRefreshToken || 
         tokenStatus.refreshTokenExpired;
};

/**
 * Get authentication status for display purposes
 * @returns Object with authentication status information
 */
export const getAuthStatus = () => {
  const tokenStatus = authService.getTokenStatus();
  
  if (!tokenStatus.hasAccessToken || !tokenStatus.hasRefreshToken) {
    return {
      status: 'unauthenticated',
      message: 'No authentication tokens found',
      action: 'login'
    };
  }
  
  if (tokenStatus.refreshTokenExpired) {
    return {
      status: 'expired',
      message: 'Session expired - please login again',
      action: 'login'
    };
  }
  
  if (tokenStatus.accessTokenExpired) {
    return {
      status: 'refreshing',
      message: 'Refreshing authentication...',
      action: 'wait'
    };
  }
  
  if (tokenStatus.needsRefresh) {
    return {
      status: 'expiring_soon',
      message: 'Authentication expiring soon',
      action: 'refresh'
    };
  }
  
  return {
    status: 'authenticated',
    message: 'Authentication valid',
    action: 'none'
  };
};

 